import "./Tips.css";
import Tips1 from "../../../Assets/Tips1.webp";
import Tips3 from "../../../Assets/Tips3.webp";
import Tips4 from "../../../Assets/Tips4.png";
import Tips5 from "../../../Assets/Tips5.png";
import Tips6 from "../../../Assets/Tips6.png";
import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { Helmet } from "react-helmet";
import axiosInstance from "../../api/axiosInstance";
import { getPreloadedData, PreloadedScript } from "../../utils/preloadedData";
import { formatDate } from "../../utils/formatDate";

// Helper function to create a URL-friendly slug from the title
const createSlug = (title) => {
  if (!title) return "";
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-"); // Replace multiple hyphens with single hyphen
};

function Tips({ isTipsPage = false }) {
  // card titles sit directly under the page's h1 on /tips (so h2), and under a section heading on the home page (h3)
  const TipTitle = isTipsPage ? "h2" : "h3";
  // Seeded from the prerendered snapshot so the first client render matches the static HTML.
  const [tips, setTips] = useState(() => getPreloadedData("__PRELOADED_TIPS__") || []);
  const [currentPage, setCurrentPage] = useState(1);
  const tipsPerPage = 3;
  const [isLoading, setIsLoading] = useState(() => getPreloadedData("__PRELOADED_TIPS__") === null);
  const [error, setError] = useState(null);
  const tipRefs = useRef([]);

  useEffect(() => {
    const fetchTips = async () => {
      setError(null);
      try {
        const response = await axiosInstance.get("api/tips");
        const sortedTips = (Array.isArray(response.data) ? response.data : []).sort(
          (a, b) => new Date(b.posted_date) - new Date(a.posted_date) || b.id - a.id
        );
        setTips(sortedTips);
      } catch (error) {
        setError("Failed to load tips. Please try again later.");
        console.error("Error fetching tips:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTips();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: "0px 0px -100px 0px",
      }
    );

    tipRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      tipRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [currentPage, tips]);

  const seoHelmet = isTipsPage && (
    <Helmet>
      <title>Tips & Tricks | 360 SynergyTech</title>
      <meta
        name="description"
        content="Explore our latest tips and tricks to enhance your knowledge. Read expert advice and stay updated with our blog."
      />
      <link rel="canonical" href="https://360synergytech.com/tips" />

      <meta property="og:title" content="Tips & Tricks | 360 SynergyTech" />
      <meta property="og:description" content="Explore our latest tips and tricks to enhance your knowledge. Read expert advice and stay updated with our blog." />
      <meta property="og:url" content="https://360synergytech.com/tips" />
      <meta property="og:image" content="https://360synergytech.com/synergy-logo.png" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Tips & Tricks | 360 SynergyTech" />
      <meta name="twitter:description" content="Explore our latest tips and tricks to enhance your knowledge. Read expert advice and stay updated with our blog." />
      <meta name="twitter:image" content="https://360synergytech.com/synergy-logo.png" />

      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Tips & Tricks Blog",
            "description": "A collection of expert tips and tricks to help you stay informed and improve your skills.",
            "url": "https://360synergytech.com/tips",
            "publisher": {
              "@type": "Organization",
              "name": "360 SynergyTech"
            }
          }
        `}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://360synergytech.com/" },
            { "@type": "ListItem", position: 2, name: "Tips", item: "https://360synergytech.com/tips" },
          ],
        })}
      </script>
    </Helmet>
  );

  const truncateText = (text, maxWords = 20, isTitle = false) => {
    const words = text.split(/\s+/);
    const limit = isTitle ? 4 : 8; // Changed to 25 words for titles
    if (words.length > limit) {
      return words.slice(0, limit).join(" ") + "...";
    }
    return text;
  };

  const totalPages = Math.ceil(tips.length / tipsPerPage);
  const indexOfLastTip = currentPage * tipsPerPage;
  const indexOfFirstTip = indexOfLastTip - tipsPerPage;
  const currentTips = tips.slice(indexOfFirstTip, indexOfLastTip);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const getPaginationItems = () => {
    const paginationItems = [];
    for (let i = 1; i <= totalPages; i++) {
      paginationItems.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`pagination_line ${currentPage === i ? "active" : ""}`}
          aria-label={`Go to page ${i}`}
        >
          <span className="visually-hidden">{`Page ${i}`}</span>
        </button>
      );
    }
    return paginationItems;
  };

  const loadingTitleHelmet = isTipsPage && <Helmet><title>Tips & Tricks | 360 SynergyTech</title></Helmet>;

  if (isLoading) return <>{loadingTitleHelmet}<div>Loading...</div></>;
  if (error) return <>{loadingTitleHelmet}<div className="error_message">{error}</div></>;
  if (!tips.length) return <>{loadingTitleHelmet}<div>No tips available.</div></>;

  return (
    <div className="tips-layout1">
      <PreloadedScript id="__PRELOADED_TIPS__" data={tips} />
      {seoHelmet}
      <div className="tips-layout2">
        <div className="tips-layout3">
          {isTipsPage ? (
            <h1 className="tips-content1 animate-fade-in">Read Our Latest Tips & Tricks</h1>
          ) : (
            <h2 className="tips-content1 animate-fade-in">Read Our Latest Tips & Tricks</h2>
          )}
        </div>
        <div className="tips-layout4">
          {/* <h2 className="tips-content2 animate-fade-in-delay">Read Our Latest Tips & Tricks</h2> */}
        </div>
        <div className="tips-layout5"></div>
        <div className="tips-layout6">
          {currentTips.length === 1 ? (
            <article
              className="tips-layout7 single-tip"
              ref={(el) => (tipRefs.current[0] = el)}
              style={{ margin: "0 auto" }}
            >
              <div className="tips-layout8">
                <div className="image_wrapper">
                  <img
                    src={`${process.env.REACT_APP_BASE_URL}/${currentTips[0].image || ""}`}
                    alt={`Image for ${currentTips[0].title || "tip"}`}
                    className="tips3"
                    onError={(e) => {
                      e.target.src = Tips3;
                    }}
                    loading="lazy"
                  />
                  <div className="image_overlay"></div>
                  <p className="tips_index_tag">{`// TIP ${String(1).padStart(2, "0")}`}</p>
                </div>
                <div className="tips-service-button">
                  <p className="tips-content3">{currentTips[0].service_name || "Example Service"}</p>
                </div>
              </div>
              <div className="tips-layout9">
                <div className="tips-layout10">
                  <div className="date-layout">
                    <img src={Tips4} alt="Date icon" className="tips4" width="16" height="16" loading="lazy" />
                    <p className="tips-content7">
                      {formatDate(currentTips[0].posted_date)}
                    </p>
                  </div>
                  <div className="tips-layout17">
                    <img src={Tips5} alt="Author icon" className="tips5" width="16" height="16" loading="lazy" />
                    <p className="tips-content8">{currentTips[0].posted_name}</p>
                  </div>
                </div>
                <div className="tips-layout11">
                  <TipTitle className="tips-content4">{truncateText(currentTips[0].title, 20, true)}</TipTitle>
                </div>
                <div className="tips-layout12">
                  <p className="tips-content5">{truncateText(currentTips[0].text, 20, false)}</p>
                </div>
                <div className="tips-layout13">
                  <Link
                    to={`/tips/${currentTips[0].id}/${createSlug(currentTips[0].title)}`}
                    className="tips-content6 btn_learn_more"
                    aria-label={`Read more about ${currentTips[0].title}`}
                  >
                    Read More
                    <img src={Tips6} alt="Arrow icon" className="tips6" width="9" height="27" loading="lazy" />
                  </Link>
                </div>
              </div>
            </article>
          ) : (
            currentTips.map((tip, index) => (
              <article
                className="tips-layout7"
                key={tip.id}
                ref={(el) => (tipRefs.current[index] = el)}
              >
                <div className="tips-layout8">
                  <div className="image_wrapper">
                    <img
                      src={`${process.env.REACT_APP_BASE_URL}/${tip.image || ""}`}
                      alt={`Image for ${tip.title || "tip"}`}
                      className="tips3"
                      onError={(e) => {
                        e.target.src = Tips3;
                      }}
                      loading="lazy"
                    />
                    <div className="image_overlay"></div>
                    <p className="tips_index_tag">{`// TIP ${String(index + 1).padStart(2, "0")}`}</p>
                  </div>
                  <div className={`tips-layout${index === 0 ? 14 : index === 1 ? 15 : 16}`}>
                    <p className="tips-content3">{tip.service_name}</p>
                  </div>
                </div>
                <div className="tips-layout9">
                  <div className="tips-layout10">
                    <div className="date-layout">
                      <img src={Tips4} alt="Date icon" className="tips4" width="16" height="16" loading="lazy" />
                      <p className="tips-content7">{formatDate(tip.posted_date)}</p>
                    </div>
                    <div className="tips-layout17">
                      <img src={Tips5} alt="Author icon" className="tips5" width="16" height="16" loading="lazy" />
                      <p className="tips-content8">{tip.posted_name}</p>
                    </div>
                  </div>
                  <div className="tips-layout11">
                    <TipTitle className="tips-content4">{truncateText(tip.title, 20, true)}</TipTitle>
                  </div>
                  <div className="tips-layout12">
                    <p className="tips-content5">{truncateText(tip.text, 20, false)}</p>
                  </div>
                  <div className="tips-layout13">
                    <Link
                      to={`/tips/${tip.id}/${createSlug(tip.title)}`}
                      className="tips-content6 btn_learn_more"
                      aria-label={`Read more about ${tip.title}`}
                    >
                      Read More
                      <img src={Tips6} alt="Arrow icon" className="tips6" width="9" height="27" loading="lazy" />
                    </Link>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
        {totalPages > 1 && (
          <div className="slider_controls">
            <button
              type="button"
              className="slider_arrow_btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous tips"
            >
              <IoChevronBack aria-hidden="true" />
            </button>
            <nav className="pagination_div" aria-label="Pagination">
              {getPaginationItems()}
            </nav>
            <button
              type="button"
              className="slider_arrow_btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Next tips"
            >
              <IoChevronForward aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Tips;